import React, { useState, useEffect } from "react";
import { Card, Form, Input, Button, Typography, message, Spin } from "antd";
import { CloseOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const LeadCaptureForm = ({ documentId, onClose, visible = true }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formSettings, setFormSettings] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Default settings fallback
  const defaultSettings = {
    formColor: "#ffffff",
    thankYouMessage: "Thank you! We'll get back to you within 24 hours.",
    formHeader: "Get Your Free Consultation",
    formSubHeader: "",
    fontFamily: "Inter, Arial, sans-serif",
    headerFontSize: 20,
    labelFontSize: 14,
    inputFontSize: 14,
    buttonFontSize: 15,
    headerColor: "#1a1a1a",
    labelColor: "#333333",
    inputColor: "#333333",
    buttonColor: "#ffffff",
    buttonBackgroundColor: "#3b82f6",
    formBorderRadius: 16,
    inputBorderRadius: 8,
    buttonBorderRadius: 8,
    formShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    inputBorderColor: "#e5e7eb",
    inputBackgroundColor: "#ffffff",
    formPadding: 24,
    formWidth: 380,
    customFields: [
      { label: "Full Name", placeholder: "Enter your full name", required: true, fieldType: "input" },
      { label: "Email Address", placeholder: "Enter your email address", required: true, fieldType: "input" },
      { label: "Phone Number", placeholder: "Enter your phone number", required: false, fieldType: "input" },
      { label: "Message", placeholder: "Tell us about your requirements", required: true, fieldType: "textarea" },
    ]
  };

  // Calculate dynamic form height based on number of fields
  const calculateFormHeight = (customFields) => {
    if (!customFields) return 300;
    
    const fieldCount = customFields.length;
    
    // Fixed heights based on number of fields
    if (fieldCount === 1) return 300;
    if (fieldCount === 2) return 400;
    if (fieldCount === 3) return 500;
    if (fieldCount >= 4) return 600;
    
    return 300; // Default fallback
  };

  // Fetch form settings from API
  const fetchFormSettings = async () => {
    if (!documentId) {
      setFormSettings(defaultSettings);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`https://user-view-backend.vercel.app/api/lead-capture-settings?documentId=${encodeURIComponent(documentId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data) {
          setFormSettings({ ...defaultSettings, ...data.data });
        } else {
          setFormSettings(defaultSettings);
        }
      } else {
        console.log('No existing settings found, using defaults');
        setFormSettings(defaultSettings);
      }
    } catch (error) {
      console.error('Error fetching form settings:', error);
      setFormSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  // Load settings on component mount
  useEffect(() => {
    if (visible) {
      fetchFormSettings();
    }
  }, [visible, documentId]);

  // Handle form submission
  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Here you can add your form submission logic
      console.log("Form submitted:", values);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setShowThankYou(true);
      
      // Auto hide thank you message and reset form after 3 seconds
      setTimeout(() => {
        setShowThankYou(false);
        form.resetFields();
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      message.error('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Don't render if not visible
  if (!visible) return null;

  // Show loading state
  if (loading || !formSettings) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // Calculate dynamic height
  const dynamicHeight = calculateFormHeight(formSettings.customFields);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <Card
        style={{
          backgroundColor: formSettings.formColor,
          width: `${formSettings.formWidth}px`,
          height: `${dynamicHeight}px`,
          maxWidth: `${formSettings.formWidth}px`,
          maxHeight: `${dynamicHeight}px`,
          boxShadow: formSettings.formShadow,
          borderRadius: `${formSettings.formBorderRadius}px`,
          border: 'none',
          fontFamily: formSettings.fontFamily,
          position: 'relative',
          overflow: 'hidden',
        }}
        bodyStyle={{ 
          padding: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: formSettings.formColor,
        }}
      >
        {/* Close Button */}
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            zIndex: 10,
            color: '#666',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />

        {!showThankYou ? (
          <div style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: formSettings.formColor,
          }}>
            {/* Header */}
            <div style={{ 
              textAlign: 'center', 
              padding: `${formSettings.formPadding}px ${formSettings.formPadding}px 16px`,
              borderBottom: '1px solid #f0f0f0',
              backgroundColor: formSettings.formColor,
              flexShrink: 0,
            }}>
              <Title 
                level={3} 
                style={{ 
                  margin: 0,
                  color: formSettings.headerColor,
                  fontSize: `${formSettings.headerFontSize}px`,
                  fontFamily: formSettings.fontFamily,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  marginBottom: formSettings.formSubHeader ? '8px' : '0',
                }}
              >
                {formSettings.formHeader}
              </Title>
              
              {formSettings.formSubHeader && (
                <Text style={{ 
                  display: 'block',
                  color: formSettings.labelColor,
                  fontSize: `${formSettings.labelFontSize}px`,
                  fontFamily: formSettings.fontFamily,
                  opacity: 0.8,
                  lineHeight: 1.4,
                  marginTop: '4px'
                }}>
                  {formSettings.formSubHeader}
                </Text>
              )}
            </div>

            {/* Form Content */}
            <div style={{
              flex: 1,
              padding: `16px ${formSettings.formPadding}px`,
              backgroundColor: formSettings.formColor,
            }}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                size="middle"
              >
                {formSettings.customFields.map((field, index) => (
                  <Form.Item
                    key={index}
                    label={
                      <span style={{ 
                        fontWeight: 500,
                        fontSize: `${formSettings.labelFontSize}px`,
                        color: formSettings.labelColor,
                        fontFamily: formSettings.fontFamily,
                      }}>
                        {field.label}
                        {field.required && <span style={{ color: '#ff4d4f', marginLeft: '2px' }}>*</span>}
                      </span>
                    }
                    name={field.label.toLowerCase().replace(/\s+/g, '_')}
                    rules={field.required ? [{ required: true, message: `Please enter ${field.label.toLowerCase()}` }] : []}
                    style={{ marginBottom: '16px' }}
                  >
                    {field.fieldType === 'textarea' ? (
                      <Input.TextArea 
                        rows={3} 
                        placeholder={field.placeholder}
                        style={{ 
                          borderRadius: `${formSettings.inputBorderRadius}px`,
                          fontSize: `${formSettings.inputFontSize}px`,
                          color: formSettings.inputColor,
                          fontFamily: formSettings.fontFamily,
                          borderColor: formSettings.inputBorderColor,
                          backgroundColor: formSettings.inputBackgroundColor,
                          padding: '12px',
                          minHeight: '80px',
                          resize: 'none'
                        }}
                      />
                    ) : (
                      <Input 
                        placeholder={field.placeholder}
                        style={{ 
                          borderRadius: `${formSettings.inputBorderRadius}px`,
                          fontSize: `${formSettings.inputFontSize}px`,
                          color: formSettings.inputColor,
                          fontFamily: formSettings.fontFamily,
                          borderColor: formSettings.inputBorderColor,
                          backgroundColor: formSettings.inputBackgroundColor,
                          padding: '12px',
                          height: '44px'
                        }}
                      />
                    )}
                  </Form.Item>
                ))}
              </Form>
            </div>

            {/* Footer with Button */}
            <div style={{
              padding: `16px ${formSettings.formPadding}px ${formSettings.formPadding}px`,
              borderTop: '1px solid #f0f0f0',
              backgroundColor: formSettings.formColor,
              flexShrink: 0,
            }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block 
                size="large"
                loading={submitting}
                onClick={() => form.submit()}
                style={{ 
                  borderRadius: `${formSettings.buttonBorderRadius}px`,
                  height: '48px',
                  fontWeight: 600,
                  background: formSettings.buttonBackgroundColor,
                  border: 'none',
                  fontSize: `${formSettings.buttonFontSize}px`,
                  color: formSettings.buttonColor,
                  fontFamily: formSettings.fontFamily,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.2s ease'
                }}
              >
                {submitting ? 'Submitting...' : 'Get My Free Consultation'}
              </Button>
            
            </div>
          </div>
        ) : (
          <div style={{ 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: formSettings.formColor,
          }}>
            <div style={{ fontSize: '64px', marginBottom: '24px', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))' }}>✨</div>
            <Title level={3} style={{ color: '#52c41a', marginBottom: '16px', fontFamily: formSettings.fontFamily }}>
              Thank You!
            </Title>
            <Text style={{ 
              fontFamily: formSettings.fontFamily, 
              fontSize: `${formSettings.labelFontSize}px`,
              color: '#666',
              lineHeight: 1.6
            }}>
              {formSettings.thankYouMessage}
            </Text>
          </div>
        )}
      </Card>
    </div>
  );
};

export default LeadCaptureForm;
